import { useCallback, useEffect, useMemo, useState } from 'react';
import { arePostalCodesEqual } from '../../shared/utils/validation';
import { SIMULATION_STEPS, SIMULATION_TITLES } from '../simulation-steps';
import { useLayout, useLockPageScroll } from '../../../../utils/useLayout';
import { devError, devLog, isBrowser } from '../../../../utils/browser';
import { useUpdateInsurance } from '../../api/use-update-insurance';
import { getErrorData } from '../../shared/utils/error';
import { useFindSimulationOptions } from '../../api/use-find-simulation-options';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useFindSimulationBestOffer } from '../../api/use-find-simulation-best-offer';
import { useLoading } from '../../../../utils/loading';
import { useInstallmentCache } from './use-installment-cache';
import EventEmitter from 'eventemitter3';
import { useFindDev } from '../../api/use-find-dev';

const TEMP_KEY = 'xasd961446da1123x';

export function useStateHandler() {
  /** @type {import('../../../../../types/SimuladorEnergiaSolar').SimulationEventHandler} */
  const EventHandler = useMemo(() => new EventEmitter(), []);

  const queriesFetching = useIsFetching();
  const mutationsPending = useIsMutating();
  const { loading: loadingState, loadingMap } = useLoading();

  const { mobile: isMobile } = useLayout();

  const [token, setToken] = useState('');
  const [offer, setOffer] = useState(null);

  const [modalTermsOpen, setModalTermsOpen] = useState(false);
  const [modalApartmentOpen, setModalApartmentOpen] = useState(false);
  const [modalRemoveInsurance, setModalRemoveInsurance] = useState(false);
  const [modalInstallments, setModalInstallments] = useState(false);
  const [modalVoucher, setModalVoucher] = useState(false);
  const [modalInsuranceDetails, setModalInsuranceDetails] = useState(false);

  const [formLanding, setFormLanding] = useState();
  const [formBasic, setFormBasic] = useState();
  const [formUsage, setFormUsage] = useState();
  const [formProposal, setFormProposal] = useState();

  const [selectedStore, setSelectedStore] = useState(null);
  const [stores, setStores] = useState();
  const [options, setOptions] = useState();

  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [roofOptions, setRoofOptions] = useState([]);
  const [resetErrors, setResetErrors] = useState([]);

  const [linkFindCEP, setLinkFindCEP] = useState('https://buscacepinter.correios.com.br');

  /** @type {import('../../../../../types/SimuladorEnergiaSolar').CEPDataState} */
  const [cepData, setCepData] = useState();

  /** @type {import('../../../../../types/SimuladorEnergiaSolar').ResidencyState} */
  const [residencyType, setResidencyType] = useState();

  /** @type {import('../../../../../types/SimuladorEnergiaSolar').SimulationStepState} */
  const [simulationStep, setSimulationStep] = useState(null);

  /** @type { import('../../../../../types/SimuladorEnergiaSolar').SimulationTypeState} */
  const [simulationType, setSimulationType] = useState();

  const [proposalId, setProposalId] = useState('');

  const findBestOffer = useFindSimulationBestOffer({ proposalId, token });
  const { mutateAsync: _updateInsurance, isPending: loadingInsurance } = useUpdateInsurance({
    token,
  });
  const findOptions = useFindSimulationOptions({ token });
  const findDev = useFindDev();

  const [headTitle, setHeadTitle] = useState(SIMULATION_TITLES.ETAPA_1);

  const handleProposalResult = useCallback((result = {}) => {
    if (result?.proposalResult === 'proposal-approved-not-partner') {
      setSimulationStep(SIMULATION_STEPS.END_JOURNEY_STORE_PROPOSAL_SENT);
      return;
    }

    if (result?.isBestOffer) {
      if (result?.proposalResult === 'proposal-failed') {
        setSimulationStep(SIMULATION_STEPS.END_JOURNEY_STORE_NOT_FOUND);
        return;
      }
    }

    setSimulationStep({
      ...SIMULATION_STEPS.END_JOURNEY_FINAL,
      variant: result?.proposalResult,
    });
  }, []);

  const restartSimulation = () => {
    setFormLanding(null);
    setFormBasic(null);
    setFormUsage(null);

    setCepData(null);
    setResidencyType(null);
    setSimulationStep(null);
    setSimulationType(null);
    setProposalId(null);
    setToken(null);

    setModalApartmentOpen(false);
    setModalTermsOpen(false);
    setModalRemoveInsurance(false);
    setModalInstallments(false);
    setModalVoucher(false);
    setModalInsuranceDetails(false);

    setStores(null);
    setOptions(null);
    setOptionsLoaded(false);
    setOffer(null);

    setSelectedStore(null);

    setRoofOptions([]);
    setResetErrors([]);

    sessionStorage.removeItem('token-energia-solar');
    sessionStorage.removeItem(TEMP_KEY);
  };

  const showErrorStep = useCallback(
    ({
      message = '',
      title = '',
      overridePreviousStep = null,
      modal = false,
      scrollTo = false,
      event = '',
      rawMessage = '',
      ctx = null,
      errorData = null,
    }) => {
      setSimulationStep({
        ...SIMULATION_STEPS.ERROR_GENERIC,
        errorData,
        ctx,
        rawMessage,
        message: message || SIMULATION_STEPS.ERROR_GENERIC.message,
        title: title || SIMULATION_STEPS.ERROR_GENERIC.title,
        previousStep: overridePreviousStep || simulationStep || null,
        modal,
        scrollTo,
        event,
      });
    },
    [simulationStep],
  );

  const refetchOffer = useCallback(async () => {
    try {
      const data = await findBestOffer.mutateAsync({});
      setOffer(data);

      if (data.finishedProposal && data.proposalResult) {
        handleProposalResult({ proposalResult: data.proposalResult, isBestOffer: true });
      }
    } catch (error) {
      const { message, rawMessage, errorData } = getErrorData(error);

      showErrorStep({
        errorData,
        rawMessage,
        message: message,
        title: 'Erro ao buscar melhor oferta.',
        overridePreviousStep: SIMULATION_STEPS.FORM_PROPOSAL,
        event: message === 'Proposta fora da fase de simulação.' ? 'reset' : 'refetch-best-offer',
        ctx: 'api-find-best-offer',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const simulationExclusiveView = !!simulationStep;

  const isCepDataValid = useMemo(() => {
    return arePostalCodesEqual(formBasic?.postalCode, cepData?.andress?.zipCode);
  }, [cepData?.andress?.zipCode, formBasic?.postalCode]);

  const loading =
    queriesFetching > 0 || mutationsPending > 0 || loadingState || findBestOffer.isPending;

  const isScrollLocked = useLockPageScroll(
    modalTermsOpen ||
      modalApartmentOpen ||
      modalRemoveInsurance ||
      (!isMobile && (modalInstallments || modalVoucher || !!modalInsuranceDetails)) ||
      loading,
  );

  const getStoreData = useCallback(
    (storeId = 0) => {
      if (!stores || stores.length === 0 || !storeId) return null;

      return stores.find((s) => String(s.id) === String(storeId)) || null;
    },
    [stores],
  );

  const exclusiveModal = isMobile && (modalInstallments || modalVoucher || !!modalInsuranceDetails);
  const hideStepper = !simulationStep || simulationStep?.hideStepper || exclusiveModal || false;
  const hideFooter = isMobile && !!modalInsuranceDetails;

  const updateInsurance = async (add = false) => {
    try {
      if (loadingInsurance) return;

      await _updateInsurance({ add });
      await refetchOffer();
    } catch (error) {
      showErrorStep({ ...getErrorData(error), ctx: 'api-update-insurance' });
    }
  };

  const installmentCache = useInstallmentCache({
    token,
    offer,
    enabled:
      simulationStep?.id === SIMULATION_STEPS.FORM_PROPOSAL.id &&
      !!offer &&
      !!token &&
      !!proposalId &&
      !loading,
    updateOfferHash: (hash) => {
      if (!offer) return;
      setOffer((prev) => ({ ...prev, hash }));
      refetchOffer();
    },
    onError: (error) => {
      const { message, rawMessage, errorData } = getErrorData(error);

      showErrorStep({
        errorData,
        message,
        rawMessage,
        title: 'Erro ao atualizar parcelas',
        event: 'refetch-best-offer',
        ctx: 'api-update-installment-cache',
      });
    },
  });

  const softRestartSimulation = (isBack = false) => {
    if (!isBack) {
      setSimulationStep(SIMULATION_STEPS.FORM_BASIC_INFO);
      setSelectedStore(null);
    }

    setProposalId(null);
    setOffer(null);
    setModalApartmentOpen(false);
    setModalTermsOpen(false);
    setModalRemoveInsurance(false);
    setModalInstallments(false);
    setModalVoucher(false);
    setModalInsuranceDetails(false);
    setOptionsLoaded(false);

    setTimeout(() => {
      installmentCache.reset();
    }, 100);
  };

  const goToSimulationStep = (payload = {}) => {
    sessionStorage.setItem(TEMP_KEY, JSON.stringify(payload));
  };

  const loadSimulationFromPreviousStep = () => {
    if (!isBrowser()) return;

    const storedData = sessionStorage.getItem(TEMP_KEY);
    if (!storedData) return;

    try {
      const p = JSON.parse(storedData);

      if (!!p?.token && !!p?.formLanding) {
        setToken(p?.token);
        setFormLanding(p?.formLanding);
        setRoofOptions(p?.roofOptions || []);
        setResetErrors(p?.resetErrors || []);
        setLinkFindCEP(p?.linkFindCEP || 'https://buscacepinter.correios.com.br');

        if (process.env.NODE_ENV === 'development') {
          if (p.proposalId && p.devSkip) {
            devLog('Loaded simulation data from previous step: ', p);
            setProposalId(p.proposalId);
            setSimulationStep(SIMULATION_STEPS.FORM_PROPOSAL.id);
            return true;
          }
        }

        setSimulationStep(SIMULATION_STEPS.FORM_BASIC_INFO);
        return true;
      }
    } catch (error) {
      devError('Error parsing stored simulation data: ', error);
    } finally {
      sessionStorage.removeItem(TEMP_KEY);
    }
  };

  const clearStateStorage = () => {
    if (!isBrowser()) return;

    sessionStorage.removeItem(TEMP_KEY);
    sessionStorage.removeItem('token-energia-solar');
  };

  const loadingProps = useMemo(() => {
    let variant = 'default';
    let message = '';
    let title = '';

    if (!!loadingMap['save-proposal']) {
      variant = 'full';
      message = 'Estamos analisando sua proposta';
      title = 'Sua jornada solar começa agora!';
    }

    if (!!loadingMap['find-final-status']) {
      variant = 'full';
      message = 'Estamos analisando sua proposta';
      title = 'Sua jornada solar começa agora!';
    }

    if (!!loadingMap['cache-regeneration']) {
      message = 'Estamos analisando sua proposta';
    }

    return {
      variant,
      title,
      message,
    };
  }, [loadingMap]);

  const loadOptions = async () => {
    try {
      const { data } = await findOptions.refetch({ cancelRefetch: true });
      setOptions(data);
    } catch (error) {
      devError('Error loading options: ', error);
    } finally {
      setOptionsLoaded(true);
    }
  };

  const loadDevData = useCallback(async () => {
    if (process.env.NODE_ENV !== 'development') return;

    const { data } = await findDev.refetch({ cancelRefetch: true, throwOnError: true });

    setToken(data.token);
    setProposalId(data.data.proposalId);
    setSimulationStep(SIMULATION_STEPS.FORM_PROPOSAL);
  }, [findDev]);

  useEffect(() => {
    if (loading) return;

    if (!token) return;

    if (simulationStep?.id === SIMULATION_STEPS.FORM_PROPOSAL.id && !optionsLoaded) {
      loadOptions();
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, optionsLoaded, simulationStep?.id, token]);

  useEffect(() => {
    if (!isBrowser() || !token) return;
    sessionStorage.setItem('token-energia-solar', token || '');
  }, [token]);

  useEffect(() => {
    if (!simulationExclusiveView || !offer) {
      return;
    }

    EventHandler.emit('offer-updated', offer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.installmentQuantity, simulationExclusiveView]);

  useEffect(() => {
    if (!simulationExclusiveView) return;

    devLog('== Setting up event handlers for simulation steps');

    let handler = EventHandler.on('retry-status', () => {
      devLog('[DEPRECATED] Event retry-status triggered');
    });

    handler = handler.on('refetch-best-offer', () => {
      devLog('Event refetch-best-offer triggered');
      setSimulationStep(SIMULATION_STEPS.FORM_PROPOSAL);
      refetchOffer();
    });

    handler = handler.on('reset', () => {
      devLog('Event reset triggered');
      restartSimulation();
    });

    handler = handler.on('soft-reset', () => {
      devLog('[DEPRECATED] Event soft-reset triggered');
    });

    if (process.env.NODE_ENV === 'development') {
      handler = handler.on('dev', () => {
        devLog('Event test-sim triggered');
        loadDevData();
      });

      handler = handler.on('reset-cache', () => {
        devLog('Event reset-cache triggered');
        installmentCache.reset();
      });

      window['simEvent'] = handler;
    }

    return () => {
      devLog('== disposing event handlers for simulation steps...');

      handler.off('retry-status');
      handler.off('refetch-best-offer');
      handler.off('reset');

      EventHandler.removeAllListeners();
    };
  }, [EventHandler, installmentCache, loadDevData, refetchOffer, simulationExclusiveView]);

  const updateOfferDetails = useCallback((details) => {
    setOffer((prev) => ({ ...prev, details }));
  }, []);

  return {
    token,
    setToken,
    goToSimulationStep,
    loadSimulationFromPreviousStep,
    clearStateStorage,
    installmentCache,

    cepData,

    formBasic,
    formLanding,
    formUsage,
    formProposal,

    modalTermsOpen,
    modalApartmentOpen,
    modalRemoveInsurance,
    modalInstallments,
    modalVoucher,
    modalInsuranceDetails,

    proposalId,
    residencyType,
    simulationStep,
    simulationType,
    exclusiveModal,

    loading,
    loadingProps,
    isCepDataValid,
    simulationExclusiveView,
    stores,
    setStores,
    offer,
    hideStepper,
    hideFooter,
    isMobile,
    isScrollLocked,
    options,
    updateOfferDetails,

    getStoreData,
    restartSimulation,
    showErrorStep,

    setCepData,

    setFormBasic,
    setFormLanding,
    setFormUsage,
    setFormProposal,

    setModalApartmentOpen,
    setModalTermsOpen,
    setModalRemoveInsurance,
    setModalInstallments,
    setModalVoucher,
    setModalInsuranceDetails,

    setProposalId,
    setResidencyType,
    setSimulationStep,
    setSimulationType,

    updateInsurance,
    handleProposalResult,
    softRestartSimulation,

    resetErrors,
    roofOptions,
    linkFindCEP,
    hasSelectedType: !!simulationType,

    selectedStore,
    setSelectedStore,

    EventHandler,

    title: headTitle,
    setTitle: setHeadTitle,
  };
}
